import { HttpClient } from '@angular/common/http';
import { Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { catchError, forkJoin, Observable, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Audience } from '../models/audience';
import { AudienceCategory } from '../models/audience-category';
import { Country } from '../models/country';
import { EventType } from '../models/event-type';
import { GraphUser } from '../models/graph-user';
import { ProductCategory } from '../models/product-category';
import { StakeholderGroup } from '../models/stakeholder-group';
import { StakeholderGroupCategory } from '../models/stakeholder-group-category';
import { TopicOfInterest } from '../models/topic-of-interest';
import { Product } from '../models/product';
import { Language } from '../models/language';


export const refDataCollections: Record<string, string> = {
  'countries': 'Countries',
  'audienceCategories': 'Audience categories',
  'audiences': 'Audiences',
  'eventTypes': 'Event types',
  'productCategories': 'Product categories',
  'products': 'Products',
  'contactStakeholderGroupCategories': 'Stakeholder group categories for contacts',
  'contactStakeholderGroups': 'Stakeholder groups for contacts',
  'orgStakeholderGroupCategories': 'Stakeholder group categories for organisations',
  'orgStakeholderGroups': 'Stakeholder groups for organisations',
  'generalTopicsOfInterest': 'General topics of interest',
  'memberTopicsOfInterest': 'Topics of interest for members',
  'graphUsers': 'Graph Users'
}

export const refDataCategories: Record<string, string> = {
  'audiences': 'audienceCategories',
  'products': 'productCategories',
  'contactStakeholderGroups': 'contactStakeholderGroupCategories',
  'orgStakeholderGroups': 'orgStakeholderGroupCategories'
}

export interface FlatRefData {
  id: number | null,
  name: string,
  lastModifiedOn?: string,
  lastModifiedBy?: string
}

export interface HierarchicalRefData {
  id: number | null,
  name: string
  categoryId?: number | null,
  categoryName?: string,
  lastModifiedOn?: string,
  lastModifiedBy?: string
}

interface ReferenceDataEntry<T> {
  signal: WritableSignal<T[]>;
  endpoint: string;
  refresh: (force?: boolean) => Observable<T[]>;
}


@Injectable({ providedIn: 'root' })
export class ReferenceDataService {
  baseUrl: string = environment.api_url;
  private readonly countries = signal(<Country[]>([]));
  private readonly audienceCategories = signal(<AudienceCategory[]>([]));
  private readonly audiences = signal(<Audience[]>([]));
  private readonly eventTypes = signal(<EventType[]>([]));
  private readonly productCategories = signal(<ProductCategory[]>([]));
  private readonly products = signal(<Product[]>([]));
  private readonly contactStakeholderGroupCategories = signal(<StakeholderGroupCategory[]>([]));
  private readonly contactStakeholderGroups = signal(<StakeholderGroup[]>([]));
  private readonly orgStakeholderGroupCategories = signal(<StakeholderGroupCategory[]>([]));
  private readonly orgStakeholderGroups = signal(<StakeholderGroup[]>([]));
  private readonly generalTopicsOfInterest = signal(<TopicOfInterest[]>([]));
  private readonly memberTopicsOfInterest = signal(<TopicOfInterest[]>([]));
  private readonly graphUsers = signal(<GraphUser[]>([]));
  private readonly languages = signal(<Language[]>([]));

  private readonly ttlMs = 60 * 60 * 1000; // TTL 1h
  private readonly lastRefreshTimestamps = new Map<string, number>();

  private readonly referenceDataMap: Record<string, ReferenceDataEntry<any>> = {
    countries: {
      signal: this.countries,
      endpoint: `${this.baseUrl}/static/countries`,
      refresh: (force = false) => this.refreshGeneric('countries', this.countries, `${this.baseUrl}/static/countries`, force),
    },
    eventTypes: {
      signal: this.eventTypes,
      endpoint: `${this.baseUrl}/static/event-types`,
      refresh: (force = false) => this.refreshGeneric('eventTypes', this.eventTypes, `${this.baseUrl}/static/event-types`, force),
    },
    audienceCategories: {
      signal: this.audienceCategories,
      endpoint: `${this.baseUrl}/static/audience-categories`,
      refresh: (force = false) => this.refreshGeneric('audienceCategories', this.audienceCategories, `${this.baseUrl}/static/audience-categories`, force),
    },
    audiences: {
      signal: this.audiences,
      endpoint: `${this.baseUrl}/static/audiences`,
      refresh: (force = false) => this.refreshGeneric('audiences', this.audiences, `${this.baseUrl}/static/audiences`, force),
    },

    productCategories: {
      signal: this.productCategories,
      endpoint: `${this.baseUrl}/static/product-categories`,
      refresh: (force = false) => this.refreshGeneric('productCategories', this.productCategories, `${this.baseUrl}/static/product-categories`, force),
    },
    products: {
      signal: this.products,
      endpoint: `${this.baseUrl}/static/products`,
      refresh: (force = false) => this.refreshGeneric('products', this.products, `${this.baseUrl}/static/products`, force),
    },
    contactStakeholderGroupCategories: {
      signal: this.contactStakeholderGroupCategories,
      endpoint: `${this.baseUrl}/static/contact-stakeholder-group-categories`,
      refresh: (force = false) => this.refreshGeneric('contactStakeholderGroupCategories', this.contactStakeholderGroupCategories, `${this.baseUrl}/static/contact-stakeholder-group-categories`, force),
    },
    contactStakeholderGroups: {
      signal: this.contactStakeholderGroups,
      endpoint: `${this.baseUrl}/static/contact-stakeholder-groups`,
      refresh: (force = false) => this.refreshGeneric('contactStakeholderGroups', this.contactStakeholderGroups, `${this.baseUrl}/static/contact-stakeholder-groups`, force),
    },
    orgStakeholderGroupCategories: {
      signal: this.orgStakeholderGroupCategories,
      endpoint: `${this.baseUrl}/static/org-stakeholder-group-categories`,
      refresh: (force = false) => this.refreshGeneric('orgStakeholderGroupCategories', this.orgStakeholderGroupCategories, `${this.baseUrl}/static/org-stakeholder-group-categories`, force),
    },
    orgStakeholderGroups: {
      signal: this.orgStakeholderGroups,
      endpoint: `${this.baseUrl}/static/org-stakeholder-groups`,
      refresh: (force = false) => this.refreshGeneric('orgStakeholderGroups', this.orgStakeholderGroups, `${this.baseUrl}/static/org-stakeholder-groups`, force),
    },
    generalTopicsOfInterest: {
      signal: this.generalTopicsOfInterest,
      endpoint: `${this.baseUrl}/static/general-topics-of-interest`,
      refresh: (force = false) => this.refreshGeneric('generalTopicsOfInterest', this.generalTopicsOfInterest, `${this.baseUrl}/static/general-topics-of-interest`, force),
    },
    memberTopicsOfInterest: {
      signal: this.memberTopicsOfInterest,
      endpoint: `${this.baseUrl}/static/member-topics-of-interest`,
      refresh: (force = false) => this.refreshGeneric('memberTopicsOfInterest', this.memberTopicsOfInterest, `${this.baseUrl}/static/member-topics-of-interest`, force),
    },
    graphUsers: {
      signal: this.graphUsers,
      endpoint: `${this.baseUrl}/azure/users`,
      refresh: (force = false) => this.refreshGeneric('graphUsers', this.graphUsers, `${this.baseUrl}/azure/users`, force),
    }
  };

  constructor(private http: HttpClient) { }

  getLanguages(): Signal<Language[]> { return this.languages; }

  getCountries(): Signal<Country[]> { return this.countries; }

  getAudienceCategories(): Signal<AudienceCategory[]> { return this.audienceCategories; }

  getAudiences(): Signal<Audience[]> { return this.audiences; }

  getEventTypes(): Signal<EventType[]> { return this.eventTypes; }

  getProductCategories(): Signal<ProductCategory[]> { return this.productCategories; }

  getProducts(): Signal<Product[]> { return this.products; }

  getContactStakeHolderGroupCategories(): Signal<StakeholderGroupCategory[]> { return this.contactStakeholderGroupCategories; }

  getContactStakeholdeGroups(): Signal<StakeholderGroup[]> { return this.contactStakeholderGroups; }

  getOrgStakeHolderGroupCategories(): Signal<StakeholderGroupCategory[]> { return this.orgStakeholderGroupCategories; }

  getOrgStakeholderGroups(): Signal<StakeholderGroup[]> { return this.orgStakeholderGroups; }

  getGeneralTopicsOfInterest(): Signal<TopicOfInterest[]> { return this.generalTopicsOfInterest; }

  getMemberTopicsOfInterest(): Signal<TopicOfInterest[]> { return this.memberTopicsOfInterest; }

  getGraphUsers(): Signal<GraphUser[]> { return this.graphUsers; }

  private refreshGeneric<T>(
    key: string,
    signal: WritableSignal<T[]>,
    endpoint: string,
    force = false
  ): Observable<T[]> {
    const last = this.lastRefreshTimestamps.get(key);

    // Return cached data as an Observable if TTL hasn't expired and force is false
    if (!force && last && Date.now() - last < this.ttlMs) {
      return of(signal());
    }

    this.lastRefreshTimestamps.set(key, Date.now());
    console.debug('refreshing', key, endpoint);

    return this.http.get<T[]>(endpoint).pipe(
      tap((data) => {
        console.log('HTTP fetch complete for:', endpoint);
        signal.set(data);
      }),
      catchError(err => {
        console.error(`Error loading reference data [${key}]:`, err);
        return of([]);
      })
    );
  }

  refreshAll(force = false): Observable<unknown> {
    // console.log('ref data refreshAll');
    const tasks: Observable<any>[] = Object.values(this.referenceDataMap).map(
      entry => entry.refresh(force)
    );

    if (tasks.length === 0) {
      return of([]);
    }

    return forkJoin(tasks);
  }

  refresh(key: string, force = true): Observable<any[]> {
    console.log('ref data refresh');
    const entry = this.referenceDataMap[key];
    if (!entry) {
      console.warn(`Reference data key [${key}] not found in referenceDataMap.`);
      return of([]);
    }
    return entry.refresh(force);
  }

  public createFlatRefData<T>(key: string, item: T): Observable<T> {
    console.debug('creating flat ref data', key, item);
    const endpoint = this.referenceDataMap[key].endpoint;
    return this.http.post<T>(endpoint, item);
  }

  public updateFlatRefData<T extends { id: number }>(key: string, item: T): Observable<T> {
    console.debug('updating flat ref data', key, item);
    const endpoint = `${this.referenceDataMap[key].endpoint}/${item.id}`;
    return this.http.put<T>(endpoint, item);
  }

  public deleteFlatRefData<T extends { id: number }>(key: string, id: number): Observable<T> {
    console.debug('deleting hierarchical ref data', key,);
    const endpoint = `${this.referenceDataMap[key].endpoint}/${id}`;
    return this.http.delete<T>(endpoint);
  }

  public createHierarchicalRefData<T>(key: string, item: T): Observable<T> {
    console.debug('creating hierarchical ref data', key, item);
    const endpoint = this.referenceDataMap[key].endpoint;
    return this.http.post<T>(endpoint, item);
  }

  public updateHierarchicalRefData<T extends { id: number }>(key: string, item: T): Observable<T> {
    console.debug('updating hierarchical ref data', key, item);
    const endpoint = `${this.referenceDataMap[key].endpoint}/${item.id}`;
    return this.http.put<T>(endpoint, item);
  }

  public deleteHierarchicalRefData<T extends { id: number }>(key: string, id: number): Observable<T> {
    console.debug('deleting hierarchical ref data', key,);
    const endpoint = `${this.referenceDataMap[key].endpoint}/${id}`;
    return this.http.delete<T>(endpoint);
  }

  // loadIfNeeded(): void {
  //   // console.log('ref data loadIfNeeded');
  //   Object.entries(this.referenceDataMap).forEach(([key, entry]) => {
  //     if (!entry.signal()) entry.refresh();
  //   });
  // }
}
